import { _js } from '@ifixit/utils/localize';
import {
   AlertDialog,
   AlertDialogAction,
   AlertDialogCancel,
   AlertDialogContent,
   AlertDialogDescription,
   AlertDialogFooter,
   AlertDialogHeader,
   AlertDialogTitle,
} from '@ifixit/ui/components/alert-dialog';
import { Checkbox } from '@ifixit/ui/components/checkbox';
import { FaIcon } from '@ifixit/ui/components/icon';
import { Input } from '@ifixit/ui/components/input';
import { ToggleGroup, ToggleGroupItem } from '@ifixit/ui/components/toggle-group';
import { cn } from '@ifixit/ui/lib/utils';
import { useId, useRef, useState } from 'react';
import type { Dispatch, HTMLAttributes, ReactNode, SetStateAction } from 'react';
import { type DocumentRow, DocumentsField } from 'Shared/editor/documents_field';
import { Field, FIELD_HINT_CLASS, SECTION_HEADING_CLASS } from 'Shared/editor/field';
import {
   ActionTooltip,
   DragHandle,
   EmptyListNotice,
   LIST_ROW_CLASS,
   RemoveButton,
   RowIconLink,
} from 'Shared/editor/list_row';
import { type EditableItem, type GuideEditFields, toEditableItem } from './guide_patch';
import {
   PrereqImportDialog,
   type PrereqImportOffer,
   type PrereqImportSelection,
} from './PrereqImportDialog';
import { usePrereqImportGuide, usePrereqTools } from './use_guide_edit_api';
import { DangerConfirmButton } from 'Shared/editor/buttons';
import { type Suggestion, suggestGuides, suggestItems } from 'Shared/editor/suggest_api';
import { SuggestInput } from 'Shared/editor/SuggestInput';
import { TimeSlider } from './TimeSlider';
import type {
   ApiGuide,
   BootPrerequisite,
   DifficultyOption,
   GuideEditMetadataProps,
   TimeIncrement,
} from './types';
import { FlagsField } from 'Shared/editor/flags_field';
import { DIFFICULTIES } from './types';
import { TOGGLE_PILL_CLASS } from 'Shared/editor/toggle_pill';
import { movedItem, type ReorderRowProps, useDragReorder } from 'Shared/editor/use_drag_reorder';
import { WikitextField, type WikitextEditor } from 'Shared/editor/wikitext_field';

/**
 * The Details tab: difficulty, time estimate, tools and parts, prerequisite
 * guides, attached documents, flags, and the conclusion. Privacy is guide
 * state, not a form field — the page header owns it (badge + Publish).
 */
export function DetailsTab({
   guide,
   langid,
   bootPrerequisites,
   timeIncrements,
   difficulties,
   flags,
   conclusionEditor,
   fields,
   onChange,
   canDelete = false,
   isDeleting = false,
   isDeleteDisabled = false,
   onDelete,
}: {
   guide: ApiGuide;
   /** The language being edited; scopes the prereq-import lookup. */
   langid: string;
   /** PHP-bootstrapped unfiltered prereq list; the source of titles. */
   bootPrerequisites: BootPrerequisite[];
   timeIncrements: TimeIncrement[];
   /** Difficulty names + descriptions, for the hint beside the picker. */
   difficulties: DifficultyOption[];
   /** Flag definitions this editor may add/remove, or null when it has none. */
   flags: GuideEditMetadataProps['flags'];
   conclusionEditor: WikitextEditor;
   fields: GuideEditFields;
   /**
    * Accepts the updater form so a handler that outlives its render (the
    * document picker's roundtrip) can spread live fields rather than the
    * ones it captured.
    */
   onChange: Dispatch<SetStateAction<GuideEditFields>>;
   /** hasDeletePrivilege — renders the delete-guide action when true. */
   canDelete?: boolean;
   /** The delete request is in flight (and the redirect that follows it). */
   isDeleting?: boolean;
   /** A guide save is using the same revision token. */
   isDeleteDisabled?: boolean;
   /** Fires after the confirm dialog; the page owns the delete + redirect. */
   onDelete?: () => void;
}) {
   const prereqTools = usePrereqTools(
      guide.guideid,
      langid,
      fields.prerequisites.map(prereq => prereq.guideid)
   );
   const missingPrereqTools = missingItems(
      (prereqTools.data ?? []).map(toEditableItem),
      fields.tools
   );

   return (
      <div className="flex flex-col gap-6">
         <div className="flex flex-col gap-6">
            <Field as="div" label={_js('Difficulty')}>
               <DifficultySelect
                  value={fields.difficulty}
                  difficulties={difficulties}
                  onChange={difficulty => onChange({ ...fields, difficulty })}
               />
            </Field>

            <TimeSlider
               increments={timeIncrements}
               minSeconds={fields.timeRequiredMin}
               maxSeconds={fields.timeRequiredMax}
               isRange={fields.timeIsRange}
               onChange={(timeRequiredMin, timeRequiredMax, timeIsRange) =>
                  onChange({ ...fields, timeRequiredMin, timeRequiredMax, timeIsRange })
               }
            />

            <ItemListEditor
               title={_js('Tools')}
               addPlaceholder={_js('Add a tool…')}
               emptyText={_js('No tools listed yet. Add the first one below.')}
               placeholderIcon="screwdriver-wrench"
               items={fields.tools}
               onChange={tools => onChange({ ...fields, tools })}
               missingFromPrereqs={missingPrereqTools}
               askPurchaseLink
            />

            <ItemListEditor
               title={_js('Parts')}
               addPlaceholder={_js('Add a part…')}
               emptyText={_js('No parts listed yet. Add the first one below.')}
               placeholderIcon="box"
               items={fields.parts}
               onChange={parts => onChange({ ...fields, parts })}
            />

            <PrerequisitesEditor
               guideid={guide.guideid}
               langid={langid}
               bootPrerequisites={bootPrerequisites}
               fields={fields}
               onChange={onChange}
            />

            <DocumentsEditor guide={guide} fields={fields} onChange={onChange} />

            {flags && (
               <FlagsField
                  available={flags.available}
                  current={flags.current}
                  orderable
                  value={fields.flags}
                  onChange={newFlags => onChange({ ...fields, flags: newFlags })}
               />
            )}
         </div>

         <WikitextField
            label={_js('Conclusion')}
            hint={_js('Shown after the last step.')}
            editor={conclusionEditor}
            preset="guide_intro"
         />

         {canDelete && onDelete && (
            <DeleteGuideSection
               isDeleting={isDeleting}
               isDisabled={isDeleteDisabled}
               onDelete={onDelete}
            />
         )}
      </div>
   );
}

/**
 * The delete-guide action, shown only to users the server would let delete
 * (hasDeletePrivilege): admins/moderators, or the sole contributor of a guide
 * with no release. Set apart in a red-bordered zone so it reads as the
 * destructive exception on a page of ordinary edits. The confirm dialog and
 * the actual delete + redirect live in DangerConfirmButton and the page.
 */
function DeleteGuideSection({
   isDeleting,
   isDisabled,
   onDelete,
}: {
   isDeleting: boolean;
   isDisabled: boolean;
   onDelete: () => void;
}) {
   return (
      <div className="rounded-lg border border-red-200 p-4">
         <h2 className={cn('mb-1', SECTION_HEADING_CLASS)}>{_js('Delete guide')}</h2>
         <p className={cn('mb-3', FIELD_HINT_CLASS)}>
            {_js(
               'Permanently deletes this guide and all its translations. Any guide that uses it as a prerequisite will have it removed. This cannot be undone.'
            )}
         </p>
         <DangerConfirmButton
            label={_js('Delete guide')}
            pendingLabel={_js('Deleting...')}
            title={_js('Delete this guide?')}
            description={_js(
               'This permanently deletes the guide, its steps, and all translations. Guides that list it as a prerequisite will have it removed. This cannot be undone.'
            )}
            isPending={isDeleting}
            disabled={isDisabled}
            onConfirm={onDelete}
         />
      </div>
   );
}

function IconToggleItem({
   value,
   icon,
   children,
}: {
   value: string;
   icon: string;
   children: ReactNode;
}) {
   return (
      <ToggleGroupItem value={value} className={TOGGLE_PILL_CLASS}>
         <FaIcon name={icon} className="size-3.5" />
         {children}
      </ToggleGroupItem>
   );
}

function DifficultySelect({
   value,
   difficulties,
   onChange,
}: {
   value: string;
   difficulties: DifficultyOption[];
   onChange: (difficulty: string) => void;
}) {
   // GET localizes the difficulty, so a translated guide can hold a value
   // that isn't in the canonical list. Capture it once so it stays
   // selectable even after the user picks a canonical value — deriving the
   // list from the live `value` would drop it the moment they switch away.
   const [seededValue] = useState(value);
   // Canonical values are what PATCH accepts; the metadata carries their
   // localized names for display.
   const canonicalOptions = DIFFICULTIES.map(canonical => ({
      value: canonical,
      label: difficulties.find(option => option.value === canonical)?.name ?? canonical,
   }));
   const options =
      seededValue && !DIFFICULTIES.includes(seededValue as (typeof DIFFICULTIES)[number])
         ? [{ value: seededValue, label: seededValue }, ...canonicalOptions]
         : canonicalOptions;
   const description = difficulties.find(
      option => option.value === value || option.name === value
   )?.description;

   return (
      <div>
         <ToggleGroup
            type="single"
            // Radix maps a single-select ToggleGroup to `group`, which leaves
            // its `radio` children without the parent that gives them "1 of 5".
            role="radiogroup"
            aria-label={_js('Difficulty')}
            value={value}
            onValueChange={next => {
               // Radix reports a re-click of the active item as ''; a guide
               // always has a difficulty, so ignore the deselect.
               if (next !== '') {
                  onChange(next);
               }
            }}
            className="flex flex-wrap gap-2"
         >
            {options.map(option => (
               <IconToggleItem
                  key={option.value}
                  value={option.value}
                  icon={DIFFICULTY_ICONS[option.value] ?? 'gauge'}
               >
                  {option.label}
               </IconToggleItem>
            ))}
         </ToggleGroup>
         {description && <p className={cn('mt-1', FIELD_HINT_CLASS)}>{description}</p>}
      </div>
   );
}

/** The gauge sweeps with the difficulty, one stop per canonical value. */
const DIFFICULTY_ICONS: Record<string, string> = {
   'Very easy': 'gauge-min',
   Easy: 'gauge-low',
   Moderate: 'gauge',
   Difficult: 'gauge-high',
   'Very difficult': 'gauge-max',
};

function ItemListEditor({
   title,
   addPlaceholder,
   emptyText,
   placeholderIcon,
   items,
   onChange,
   missingFromPrereqs = [],
   askPurchaseLink = false,
}: {
   title: string;
   addPlaceholder: string;
   /** Shown instead of rows while the list is empty, so a bare input under a
       heading doesn't read as "something failed to load". */
   emptyText: string;
   /** Font Awesome icon shown for items without a catalog thumbnail. */
   placeholderIcon: string;
   items: EditableItem[];
   onChange: (items: EditableItem[]) => void;
   /** Tools the prerequisite guides use and this list doesn't have yet;
       non-empty renders the standing import notice above the rows. */
   missingFromPrereqs?: EditableItem[];
   /** Offer a purchase-link field when confirming a new off-catalog item
       (tools, which are things you buy); parts leave it off. */
   askPurchaseLink?: boolean;
}) {
   const [newName, setNewName] = useState('');
   // A typed name that isn't in the item catalog, awaiting the "add it
   // anyway?" confirmation; non-null renders the dialog.
   const [confirmName, setConfirmName] = useState<string | null>(null);
   // The purchase link typed in that confirmation, for a new (off-catalog)
   // tool; a catalog item carries its own item-page link instead.
   const [purchaseUrl, setPurchaseUrl] = useState('');
   const [isCheckingCatalog, setIsCheckingCatalog] = useState(false);
   // The row a duplicate add bounced off of, briefly ringed so the no-op
   // reads as "it's already here" instead of a silent failure.
   const [flashedIndex, setFlashedIndex] = useState<number | null>(null);
   const flashTimeoutRef = useRef<number | null>(null);
   const updateItem = (index: number, update: Partial<EditableItem>) => {
      onChange(items.map((item, i) => (i === index ? { ...item, ...update } : item)));
   };
   // Featuring promotes an item to the front: the API only lets the first
   // item be featured, so "feature this" and "make this the lead item" are
   // the same action. Un-featuring leaves the item where it is.
   const featureItem = (index: number) => {
      const target = items[index];
      onChange(
         target.featured
            ? items.map((item, i) => (i === index ? { ...item, featured: false } : item))
            : [
                 { ...target, featured: true },
                 ...items.filter((_, i) => i !== index).map(item => ({ ...item, featured: false })),
              ]
      );
   };
   // Reordering clears the featured flag on any row it leaves below the
   // top: only the first item may be featured (itemsPayload would strip the
   // flag on save anyway), and keeping it in state left the star showing on
   // a row the save was about to silently un-feature.
   const drag = useDragReorder((from, to) =>
      onChange(
         movedItem(items, from, to).map((item, index) =>
            index > 0 && item.featured ? { ...item, featured: false } : item
         )
      )
   );

   // Rows save under wikiTitle when they have one, and the server rejects
   // duplicate saved names, so two rows that display differently can still
   // collide on save ("Clampy - Anti-Clamp" saves as "Anti-Clamp"). Compare
   // display and saved names on both sides.
   const duplicateIndexOf = (name: string, wikiTitle?: string | null) => {
      const candidates = new Set(
         [name, wikiTitle].filter((n): n is string => !!n).map(normalizeItemName)
      );
      return items.findIndex(
         item =>
            candidates.has(normalizeItemName(item.name)) ||
            (!!item.wikiTitle && candidates.has(normalizeItemName(item.wikiTitle)))
      );
   };

   const flashExistingRow = (index: number) => {
      setFlashedIndex(index);
      if (flashTimeoutRef.current !== null) {
         window.clearTimeout(flashTimeoutRef.current);
      }
      flashTimeoutRef.current = window.setTimeout(() => setFlashedIndex(null), 1200);
   };

   const addItem = (
      name: string,
      { imageUrl = null, url = '', wikiUrl = null, wikiTitle = null }: Partial<EditableItem> = {}
   ) => {
      const trimmed = name.trim();
      if (trimmed === '') {
         return;
      }
      // Adding a name already in the list points at the existing row instead
      // of appending a twin.
      const duplicateIndex = duplicateIndexOf(trimmed, wikiTitle);
      if (duplicateIndex !== -1) {
         setNewName('');
         flashExistingRow(duplicateIndex);
         return;
      }
      onChange([
         ...items,
         {
            name: trimmed,
            type: '',
            quantity: 1,
            notes: '',
            url: url.trim(),
            isoptional: false,
            featured: false,
            imageUrl,
            wikiUrl,
            wikiTitle,
         },
      ]);
      setNewName('');
   };

   // Confirm the off-catalog add, carrying any purchase link, and reset the
   // dialog. Shared by the Add-it button and Enter in the purchase-link field.
   const confirmAdd = () => {
      if (confirmName !== null) {
         addItem(confirmName, { url: purchaseUrl });
      }
      setConfirmName(null);
      setPurchaseUrl('');
   };

   // Enter on typed text (not a suggestion pick): names already in the item
   // catalog add straight away; anything else confirms first, since freehand
   // names are how duplicate items multiply.
   const commitTypedName = async (name: string) => {
      const trimmed = name.trim();
      if (trimmed === '' || isCheckingCatalog) {
         return;
      }
      // Duplicates short-circuit before the catalog lookup, so a re-typed
      // freehand name can't reach the "add it anyway?" dialog.
      const duplicateIndex = duplicateIndexOf(trimmed);
      if (duplicateIndex !== -1) {
         setNewName('');
         flashExistingRow(duplicateIndex);
         return;
      }
      setIsCheckingCatalog(true);
      try {
         const match = await findCatalogItem(trimmed);
         if (match === null) {
            setConfirmName(trimmed);
         } else if (match === 'unknown') {
            addItem(trimmed);
         } else {
            // The catalog's spelling, not the typed one: a reload rewrites the
            // name from the wiki anyway, and the row shows the match's photo.
            addItem(match.label, {
               imageUrl: match.imageUrl,
               wikiUrl: match.wikiUrl ?? null,
               wikiTitle: match.wikiTitle ?? null,
            });
         }
      } finally {
         setIsCheckingCatalog(false);
      }
   };

   // Suggestion keys are catalog wikiids, which the rows don't carry, so
   // excludeKeys can't hide already-listed items; filtering on the normalized
   // label does the same job.
   const suggestNewItems = async (query: string) => {
      const existingNames = new Set(
         items.flatMap(item => [
            normalizeItemName(item.name),
            ...(item.wikiTitle ? [normalizeItemName(item.wikiTitle)] : []),
         ])
      );
      const options = await suggestItems(query);
      return options.filter(
         option =>
            !existingNames.has(normalizeItemName(option.label)) &&
            !(option.wikiTitle && existingNames.has(normalizeItemName(option.wikiTitle)))
      );
   };

   // Imports land on top, but under a featured row: only the first item may
   // be featured, and the save strips the flag off any row below it.
   const importMissing = () => {
      const insertAt = items[0]?.featured ? 1 : 0;
      onChange([...items.slice(0, insertAt), ...missingFromPrereqs, ...items.slice(insertAt)]);
   };

   return (
      <div>
         <h2 className={cn('mb-1', SECTION_HEADING_CLASS)}>{title}</h2>
         {missingFromPrereqs.length > 0 && (
            <MissingToolsNotice count={missingFromPrereqs.length} onImport={importMissing} />
         )}
         {items.length === 0 && (
            <EmptyListNotice icon={placeholderIcon}>{emptyText}</EmptyListNotice>
         )}
         <div className="flex flex-col gap-2">
            <span aria-live="polite" className="sr-only">
               {drag.announcement}
            </span>
            {drag.displayItems(items).map(({ item, sourceIndex }, displayIndex) => (
               <ItemRow
                  key={sourceIndex}
                  item={item}
                  placeholderIcon={placeholderIcon}
                  removeLabel={_js('Remove %1', item.name || title)}
                  rowProps={drag.rowProps(displayIndex)}
                  handleProps={drag.handleProps(displayIndex, {
                     label: _js('Reorder %1 (use the arrow keys to move it)', item.name || title),
                  })}
                  isDragging={drag.draggingIndex === displayIndex}
                  isFlashed={flashedIndex === sourceIndex}
                  onUpdate={update => updateItem(sourceIndex, update)}
                  onToggleFeatured={() => featureItem(sourceIndex)}
                  onRemove={() => onChange(items.filter((_, i) => i !== sourceIndex))}
               />
            ))}
         </div>
         {/* No "Add" button: a trailing blank input adds a row as soon as a
             name is entered (picked from the suggestions or typed + Enter). */}
         <SuggestInput
            queryKey="guide-item-suggest"
            className="mt-2 w-full"
            ariaLabel={_js('Add %1', title)}
            placeholder={addPlaceholder}
            value={newName}
            onValueChange={setNewName}
            onSelect={suggestion =>
               addItem(suggestion.label, {
                  imageUrl: suggestion.imageUrl,
                  wikiUrl: suggestion.wikiUrl ?? null,
                  wikiTitle: suggestion.wikiTitle ?? null,
               })
            }
            onKeyDown={event => {
               if (event.key !== 'Enter') {
                  return;
               }

               event.preventDefault();
               void commitTypedName(newName);
            }}
            fetchSuggestions={suggestNewItems}
         />

         <AlertDialog
            open={confirmName !== null}
            onOpenChange={open => {
               if (open) {
                  return;
               }

               setConfirmName(null);
               setPurchaseUrl('');
            }}
         >
            <AlertDialogContent>
               <AlertDialogHeader>
                  <AlertDialogTitle>{_js('Add "%1"?', confirmName ?? '')}</AlertDialogTitle>
                  <AlertDialogDescription>
                     {_js(
                        "We don't have this %1 in our catalog yet. Are you sure you want to add it?",
                        confirmName ?? ''
                     )}
                  </AlertDialogDescription>
               </AlertDialogHeader>
               {askPurchaseLink && (
                  <Field
                     label={_js('Purchase link')}
                     hint={_js('Optional. Where someone can buy this tool.')}
                  >
                     <Input
                        type="url"
                        value={purchaseUrl}
                        placeholder={_js('https://…')}
                        onChange={event => setPurchaseUrl(event.target.value)}
                        onKeyDown={event => {
                           if (event.key !== 'Enter') {
                              return;
                           }

                           event.preventDefault();
                           confirmAdd();
                        }}
                     />
                  </Field>
               )}
               <AlertDialogFooter>
                  <AlertDialogCancel>{_js('Keep searching')}</AlertDialogCancel>
                  <AlertDialogAction onClick={confirmAdd}>{_js('Add it')}</AlertDialogAction>
               </AlertDialogFooter>
            </AlertDialogContent>
         </AlertDialog>
      </div>
   );
}

/**
 * One click takes every missing tool, with no confirm step: you need a
 * prerequisite's tools to do the prerequisite.
 */
function MissingToolsNotice({ count, onImport }: { count: number; onImport: () => void }) {
   return (
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2 rounded-md border border-brand-200 bg-brand-50 px-3 py-2 text-sm text-gray-700">
         <span>
            {count === 1
               ? _js('Prerequisite guides use 1 tool that is not on this guide.')
               : _js('Prerequisite guides use %1 tools that are not on this guide.', String(count))}
         </span>
         <button type="button" onClick={onImport} className="button-sm button-outline">
            {_js('Import missing tools')}
         </button>
      </div>
   );
}

/**
 * The item-catalog entry a typed name matches, through the same suggest index
 * the dropdown uses, so typing a name links the page picking it would have.
 * 'unknown' on a search outage, which counts as a match so the outage never
 * blocks adding an item.
 */
async function findCatalogItem(name: string): Promise<Suggestion | null | 'unknown'> {
   const normalize = (title: string) =>
      title
         .trim()
         .toLowerCase()
         .replace(/[_\s]+/g, ' ');
   try {
      const options = await suggestItems(name);
      const matches = (option: Suggestion) =>
         normalize(option.label) === normalize(name) ||
         (!!option.wikiTitle && normalize(option.wikiTitle) === normalize(name));
      return options.find(matches) ?? null;
   } catch {
      return 'unknown';
   }
}

/**
 * Where an item's thumbnail links. A wiki url wins whenever the row has one,
 * from the GET or from the picked suggestion: `url` prefers the store product
 * page (GuideItem::get_url()), and the wiki title can differ from the display
 * name, so neither can stand in for it. Without one the item has no page yet
 * and the thumbnail opens the create-item flow. The slug mirrors the server's
 * link-title rule (spaces to underscores, then url-encoded).
 */
function itemPageLink(item: EditableItem): { href: string; isCreate: boolean } | null {
   const name = item.name.trim();
   if (name === '') {
      return null;
   }
   if (item.wikiUrl) {
      return { href: item.wikiUrl, isCreate: false };
   }
   const slug = encodeURIComponent(name.replace(/ /g, '_'));
   return { href: `/Item/Edit/${slug}`, isCreate: true };
}

/**
 * The item's catalog photo, or a generic icon when it has none, linking to the
 * item wiki (or the create-item flow for names not in the catalog). Sized to
 * the name input's height so their top and bottom edges line up.
 */
/** The duplicate-detection key: trimmed, lowercased, whitespace collapsed. */
function normalizeItemName(name: string): string {
   return name.trim().toLowerCase().replace(/\s+/g, ' ');
}

function ItemThumbnail({ item, placeholderIcon }: { item: EditableItem; placeholderIcon: string }) {
   const image = item.imageUrl ? (
      <img
         src={item.imageUrl}
         alt=""
         className="size-full rounded border border-solid border-gray-300 object-cover"
      />
   ) : (
      <span className="flex size-full items-center justify-center rounded border border-gray-300 bg-gray-100 text-gray-400">
         <FaIcon name={placeholderIcon} className="size-4" />
      </span>
   );

   const link = itemPageLink(item);
   if (!link) {
      return <span className="size-9 shrink-0">{image}</span>;
   }

   const label = link.isCreate
      ? _js('Create the item page for %1', item.name)
      : _js('Open item in new tab');
   return (
      <ActionTooltip label={label}>
         <a
            href={link.href}
            target="_blank"
            rel="noreferrer"
            aria-label={label}
            // The ring (not just an opacity dip) says "this is a link" —
            // nothing else about a thumbnail advertises that it opens the
            // item's page.
            className="block size-9 shrink-0 rounded outline-none hover:ring-2 hover:ring-brand-300 focus-visible:ring-[3px] focus-visible:ring-ring/50"
         >
            {image}
         </a>
      </ActionTooltip>
   );
}

/**
 * One tool/part row. The name reads as text and edits on click — the same
 * read-mode ⇄ input swap the step lines use, so the list reads as names rather
 * than a column of empty-looking inputs, and renaming (the rarest action here)
 * doesn't look like the primary one. Quantity and the Optional flag stay
 * visible beside it — a compact − n + stepper and a checkbox — wrapping below
 * as a right-aligned group when the name runs long.
 */
function ItemRow({
   item,
   placeholderIcon,
   removeLabel,
   rowProps,
   handleProps,
   isDragging,
   isFlashed,
   onUpdate,
   onToggleFeatured,
   onRemove,
}: {
   item: EditableItem;
   placeholderIcon: string;
   removeLabel: string;
   rowProps: ReorderRowProps;
   handleProps: HTMLAttributes<HTMLSpanElement>;
   isDragging: boolean;
   /** Briefly true after a duplicate add bounced off this row. */
   isFlashed: boolean;
   onUpdate: (update: Partial<EditableItem>) => void;
   onToggleFeatured: () => void;
   onRemove: () => void;
}) {
   const [isEditingName, setIsEditingName] = useState(false);
   // The name as it was when editing began, so abandoning an edit with the
   // field cleared can't strand an unnamed row.
   const nameBeforeEditRef = useRef(item.name);
   // Its catalog page, photo, and store link alongside it: all four fields
   // describe that name, so retyping the name has to bring them back rather
   // than leave the row on the name-derived fallback.
   const catalogBeforeEditRef = useRef({
      wikiUrl: item.wikiUrl ?? null,
      wikiTitle: item.wikiTitle ?? null,
      imageUrl: item.imageUrl ?? null,
      url: item.url,
   });
   const nameButtonRef = useRef<HTMLButtonElement>(null);

   const beginEditingName = () => {
      nameBeforeEditRef.current = item.name;
      catalogBeforeEditRef.current = {
         wikiUrl: item.wikiUrl ?? null,
         wikiTitle: item.wikiTitle ?? null,
         imageUrl: item.imageUrl ?? null,
         url: item.url,
      };
      setIsEditingName(true);
   };

   // A typed name no longer describes the item the server sent, so the page
   // it links, the title it saves under, its photo, and its store link no
   // longer apply; typing the original name back restores all four. A kept
   // `url` would do more than mislink: the save sends it, and createWiki
   // attaches it as the supplier row of the page it creates for the new name.
   const renameTo = (name: string) => ({
      name,
      ...(name.trim() === nameBeforeEditRef.current.trim()
         ? catalogBeforeEditRef.current
         : { wikiUrl: null, wikiTitle: null, imageUrl: null, url: '' }),
   });

   // refocus: keyboard exits (Enter/Escape/pick) return focus to the name
   // button so a keyboard user isn't dropped; blur exits don't steal focus
   // back from wherever the user clicked.
   const endEditingName = (refocus: boolean) => {
      setIsEditingName(false);
      if (item.name.trim() === '') {
         onUpdate(renameTo(nameBeforeEditRef.current));
      }
      if (refocus) {
         requestAnimationFrame(() => nameButtonRef.current?.focus());
      }
   };

   return (
      <div
         {...rowProps}
         className={cn(
            // Bordered like every other row list on this page, with the
            // quantity and optional controls visible in the row; flex-wrap
            // drops them below a long name on narrow screens.
            LIST_ROW_CLASS,
            'group flex-wrap',
            isDragging && 'opacity-60 ring-2 ring-brand-300',
            isFlashed && 'ring-2 ring-brand-300'
         )}
      >
         <DragHandle {...handleProps} />
         <ItemThumbnail item={item} placeholderIcon={placeholderIcon} />
         {isEditingName ? (
            <SuggestInput
               queryKey="guide-item-suggest"
               className="min-w-40 flex-1"
               ariaLabel={_js('Name')}
               value={item.name}
               autoFocus
               onValueChange={name => onUpdate(renameTo(name))}
               onSelect={suggestion => {
                  onUpdate({
                     name: suggestion.label,
                     imageUrl: suggestion.imageUrl,
                     wikiUrl: suggestion.wikiUrl ?? null,
                     wikiTitle: suggestion.wikiTitle ?? null,
                  });
                  endEditingName(true);
               }}
               fetchSuggestions={suggestItems}
               onBlur={() => endEditingName(false)}
               onKeyDown={event => {
                  // The dropdown consumes Enter-on-a-highlight and the first
                  // Escape itself; what reaches here ends the edit.
                  if (event.key !== 'Enter' && event.key !== 'Escape') {
                     return;
                  }
                  event.preventDefault();
                  endEditingName(true);
               }}
            />
         ) : (
            <ActionTooltip label={_js('Edit name')}>
               <button
                  ref={nameButtonRef}
                  type="button"
                  onClick={beginEditingName}
                  // Long catalog names wrap to two lines instead of truncating
                  // away the part that distinguishes them; min-w-40 keeps the
                  // name from being crushed before the controls wrap under it.
                  className="min-w-40 flex-1 cursor-text rounded px-2 py-1 text-left text-sm text-gray-900 outline-none hover:bg-gray-100 focus-visible:ring-[3px] focus-visible:ring-ring/50"
               >
                  <span className="line-clamp-2">{item.name}</span>
               </button>
            </ActionTooltip>
         )}
         {/* One flex group, so a long name wraps quantity, optional, and
             remove together as a right-aligned unit instead of scattering. */}
         <div className="ml-auto flex shrink-0 items-center gap-2">
            <QuantityStepper value={item.quantity} onChange={quantity => onUpdate({ quantity })} />
            <ActionTooltip
               label={
                  item.featured ? _js('Featured item') : _js('Feature this item (moves it first)')
               }
            >
               <button
                  type="button"
                  aria-label={
                     item.featured
                        ? _js('Featured item')
                        : _js('Feature this item (moves it first)')
                  }
                  aria-pressed={item.featured}
                  onClick={onToggleFeatured}
                  className={cn(
                     'flex size-7 shrink-0 items-center justify-center rounded outline-none hover:bg-gray-100 focus-visible:ring-[3px] focus-visible:ring-ring/50',
                     // Only one item per list can be featured (the lead item);
                     // the filled amber star marks it, gray-300 reads as "not
                     // featured, click to make this the featured item".
                     item.featured ? 'text-yellow-500' : 'text-gray-300'
                  )}
               >
                  <FaIcon name="star" className="size-3.5" />
               </button>
            </ActionTooltip>
            <label className="flex shrink-0 cursor-pointer items-center gap-1.5 text-xs text-gray-600">
               <Checkbox
                  className="size-4 shrink-0"
                  checked={item.isoptional}
                  onCheckedChange={checked => onUpdate({ isoptional: checked })}
               />
               <span>{_js('Optional')}</span>
            </label>
            <RemoveButton label={removeLabel} onClick={onRemove} />
         </div>
      </div>
   );
}

/**
 * The row's quantity control: a compact − n + stepper, always visible so
 * quantity never hides behind a focus state. The numeral stays muted at the
 * default 1 and darkens once it means something. Typing works too, with a
 * digits-only draft so clearing the field doesn't snap back mid-keystroke.
 * (No stepper exists in @ifixit/ui yet — promote this when one lands.)
 */
function QuantityStepper({ value, onChange }: { value: number; onChange: (n: number) => void }) {
   const [draft, setDraft] = useState<string | null>(null);
   const inputId = useId();
   return (
      <div className="flex h-7 shrink-0 items-center rounded-lg border border-gray-300">
         <button
            type="button"
            aria-label={_js('Decrease quantity')}
            disabled={value <= 1}
            onClick={() => onChange(Math.max(1, value - 1))}
            className="flex h-full w-6 items-center justify-center rounded-l-lg text-gray-500 outline-none hover:bg-gray-100 hover:text-gray-800 focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:opacity-30 disabled:hover:bg-transparent"
         >
            <FaIcon name="minus" className="size-3" />
         </button>
         <input
            id={inputId}
            type="text"
            inputMode="numeric"
            aria-label={_js('Quantity')}
            value={draft ?? String(value)}
            onChange={event => {
               const digits = event.target.value.replace(/\D/g, '');
               setDraft(digits);
               if (digits !== '') {
                  onChange(Math.max(1, Number(digits)));
               }
            }}
            onBlur={() => setDraft(null)}
            className={cn(
               'h-full w-7 border-0 bg-transparent p-0 text-center text-sm tabular-nums outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50',
               value === 1 ? 'text-gray-600' : 'text-gray-900'
            )}
         />
         <button
            type="button"
            aria-label={_js('Increase quantity')}
            onClick={() => onChange(value + 1)}
            className="flex h-full w-6 items-center justify-center rounded-r-lg text-gray-500 outline-none hover:bg-gray-100 hover:text-gray-800 focus-visible:ring-[3px] focus-visible:ring-ring/50"
         >
            <FaIcon name="plus" className="size-3" />
         </button>
      </div>
   );
}

function PrerequisitesEditor({
   guideid,
   langid,
   bootPrerequisites,
   fields,
   onChange,
}: {
   guideid: number;
   langid: string;
   bootPrerequisites: BootPrerequisite[];
   fields: GuideEditFields;
   onChange: (fields: GuideEditFields) => void;
}) {
   const [query, setQuery] = useState('');
   const [searchAllGuides, setSearchAllGuides] = useState(false);
   // Titles for prereqs picked this session; the initial ones come from the
   // bootstrapped list. A null title means the current user can't view that
   // prereq — it still must survive the save untouched.
   const [pickedTitles, setPickedTitles] = useState<Record<number, string>>({});
   // Session overrides for prereqs picked this session; the initial rows get
   // their thumbnail from the bootstrapped list, so it survives a save.
   const [pickedImages, setPickedImages] = useState<Record<number, string>>({});
   // The just-picked prereq whose own content is being offered for import;
   // set on pick, cleared when the offer is applied or skipped.
   const [importSource, setImportSource] = useState<{ guideid: number; title: string } | null>(
      null
   );
   const importQuery = usePrereqImportGuide(importSource?.guideid ?? null, langid);

   const bootPrereq = (prereqGuideid: number) =>
      bootPrerequisites.find(prereq => prereq.guideid === prereqGuideid);

   const titleFor = (prereqGuideid: number) =>
      pickedTitles[prereqGuideid] ??
      bootPrereq(prereqGuideid)?.title ??
      _js('Guide %1 (not visible to you)', String(prereqGuideid));

   // A picked guide's image, else the bootstrapped thumbnail; null (no image
   // or not visible) renders a placeholder box.
   const imageFor = (prereqGuideid: number) =>
      pickedImages[prereqGuideid] ?? bootPrereq(prereqGuideid)?.imageUrl ?? null;

   const prereqGuideids = fields.prerequisites.map(prereq => prereq.guideid);
   const drag = useDragReorder((from, to) =>
      onChange({ ...fields, prerequisites: movedItem(fields.prerequisites, from, to) })
   );

   // What the picked prereq has to offer, against the *current* fields: its
   // own prerequisites (minus this guide — immediate circle — and ones
   // already listed) and the tools/parts this guide doesn't list yet. The
   // dialog shows only while the pick is still listed — removing the row
   // mid-fetch withdraws the offer.
   const offer: PrereqImportOffer | null =
      importSource && importQuery.data && prereqGuideids.includes(importSource.guideid)
         ? {
              sourceTitle: importSource.title,
              prerequisites: importQuery.data.prerequisites
                 .filter(
                    prereq => prereq.guideid !== guideid && !prereqGuideids.includes(prereq.guideid)
                 )
                 .map(prereq => ({
                    guideid: prereq.guideid,
                    title: prereq.title,
                    imageUrl:
                       prereq.image?.thumbnail ??
                       prereq.image?.standard ??
                       prereq.image?.mini ??
                       null,
                 })),
              tools: missingItems(importQuery.data.tools.map(toEditableItem), fields.tools),
              parts: missingItems(importQuery.data.parts.map(toEditableItem), fields.parts),
           }
         : null;
   const offerIsEmpty =
      offer !== null &&
      offer.prerequisites.length === 0 &&
      offer.tools.length === 0 &&
      offer.parts.length === 0;

   const applyImport = (selection: PrereqImportSelection) => {
      if (!importSource) {
         return;
      }
      setImportSource(null);
      // Re-filter against the fields as they are *now* — the offer was built
      // before the user confirmed, and nothing pins the form in between.
      const addedPrereqs = selection.prerequisites.filter(
         prereq => prereq.guideid !== guideid && !prereqGuideids.includes(prereq.guideid)
      );
      const addedTools = missingItems(selection.tools, fields.tools);
      const addedParts = missingItems(selection.parts, fields.parts);
      if (addedPrereqs.length === 0 && addedTools.length === 0 && addedParts.length === 0) {
         return;
      }

      setPickedTitles(titles => {
         const next = { ...titles };
         for (const prereq of addedPrereqs) {
            next[prereq.guideid] = prereq.title;
         }
         return next;
      });
      setPickedImages(images => {
         const next = { ...images };
         for (const prereq of addedPrereqs) {
            if (prereq.imageUrl !== null) {
               next[prereq.guideid] = prereq.imageUrl;
            }
         }
         return next;
      });

      // Steps inline in list order and the source guide expects its own
      // prerequisites done first, so they go in front of it (v1 did the
      // same). If the source row is gone, they just append.
      const sourceIndex = fields.prerequisites.findIndex(
         prereq => prereq.guideid === importSource.guideid
      );
      const rows = addedPrereqs.map(prereq => ({ guideid: prereq.guideid, releaseid: null }));
      const prerequisites =
         sourceIndex === -1
            ? [...fields.prerequisites, ...rows]
            : [
                 ...fields.prerequisites.slice(0, sourceIndex),
                 ...rows,
                 ...fields.prerequisites.slice(sourceIndex),
              ];

      onChange({
         ...fields,
         prerequisites,
         tools: [...fields.tools, ...addedTools],
         parts: [...fields.parts, ...addedParts],
      });
   };

   return (
      <div>
         <h2 className={cn('mb-1', SECTION_HEADING_CLASS)}>{_js('Prerequisite Guides')}</h2>
         <p className="mb-2 text-xs text-gray-500">
            {_js('Prerequisites are inserted before the steps of this guide.')}
         </p>
         <span aria-live="polite" className="sr-only">
            {drag.announcement}
         </span>
         <ul className="m-0 flex list-none flex-col gap-1 p-0">
            {drag
               .displayItems(fields.prerequisites)
               .map(({ item: prereq, sourceIndex }, displayIndex) => {
                  const thumbnail = imageFor(prereq.guideid);
                  return (
                     <li
                        key={prereq.guideid}
                        {...drag.rowProps(displayIndex, { draggable: true })}
                        className={cn(
                           LIST_ROW_CLASS,
                           'group text-sm',
                           drag.draggingIndex === displayIndex && 'opacity-60 ring-2 ring-brand-300'
                        )}
                     >
                        <DragHandle
                           {...drag.handleProps(displayIndex, {
                              label: _js(
                                 'Reorder %1 (use the arrow keys to move it)',
                                 titleFor(prereq.guideid)
                              ),
                           })}
                        />
                        {/* A thumb leads the row like the tool/part rows; a prereq
                         with no image (or not visible) gets a placeholder box
                         so the titles stay aligned. */}
                        {thumbnail === null ? (
                           <span className="flex size-9 shrink-0 items-center justify-center rounded border border-gray-300 bg-gray-100 text-gray-400">
                              <FaIcon name="list-ol" className="size-4" />
                           </span>
                        ) : (
                           <img
                              src={thumbnail}
                              alt=""
                              className="size-9 shrink-0 rounded border border-solid border-gray-300 object-cover"
                           />
                        )}
                        {/* Guide titles run long; two lines beats losing the
                         distinguishing tail to a truncation ellipsis. */}
                        <span className="line-clamp-2 min-w-0 flex-1">
                           {titleFor(prereq.guideid)}
                        </span>
                        <RowIconLink
                           label={_js('View guides using this prerequisite')}
                           href={`/Guide/prereq/${prereq.guideid}`}
                           icon="layer-group"
                           iconClassName="size-3.5"
                        />
                        <RowIconLink
                           label={_js('Open guide in a new tab')}
                           href={`/Guide/view/redirect/to/${prereq.guideid}`}
                           icon="arrow-up-right-from-square"
                           iconClassName="size-3.5"
                        />
                        <RemoveButton
                           label={_js('Remove prerequisite')}
                           onClick={() =>
                              onChange({
                                 ...fields,
                                 prerequisites: fields.prerequisites.filter(
                                    (_, index) => index !== sourceIndex
                                 ),
                              })
                           }
                        />
                     </li>
                  );
               })}
         </ul>
         <SuggestInput
            queryKey="guide-prerequisite-suggest"
            className="mt-2"
            value={query}
            onValueChange={setQuery}
            placeholder={_js('Search for a guide by title...')}
            fetchSuggestions={searchQuery =>
               suggestGuides(searchQuery, searchAllGuides ? undefined : fields.category)
            }
            searchKey={`${searchAllGuides ? 'all' : 'device'}:${fields.category}`}
            listAction={{
               label: searchAllGuides
                  ? _js('Search guides for this device')
                  : _js('Search all guides'),
               icon: searchAllGuides ? 'search-minus' : 'search-plus',
               onSelect: () => setSearchAllGuides(current => !current),
            }}
            excludeKeys={[...prereqGuideids, guideid]}
            onSelect={suggestion => {
               const pickedGuideid = Number(suggestion.key);
               setPickedTitles(titles => ({ ...titles, [pickedGuideid]: suggestion.label }));
               const pickedImage = suggestion.imageUrl;
               if (pickedImage) {
                  setPickedImages(images => ({ ...images, [pickedGuideid]: pickedImage }));
               }
               if (!prereqGuideids.includes(pickedGuideid)) {
                  onChange({
                     ...fields,
                     // New prereqs track the latest release (releaseid null);
                     // existing pins ride along untouched.
                     prerequisites: [
                        ...fields.prerequisites,
                        { guideid: pickedGuideid, releaseid: null },
                     ],
                  });
                  // The pick lands immediately; the offer to bring along the
                  // picked guide's own prereqs/tools/parts follows once its
                  // data arrives (nothing shows if it has none to offer).
                  setImportSource({ guideid: pickedGuideid, title: suggestion.label });
               }
               setQuery('');
            }}
         />
         {offer && !offerIsEmpty && (
            // Keyed by the source so a new pick starts with a fresh
            // selection: its prereqs and tools checked, its parts not.
            <PrereqImportDialog
               key={importSource?.guideid}
               offer={offer}
               onConfirm={applyImport}
               onSkip={() => setImportSource(null)}
            />
         )}
      </div>
   );
}

/**
 * The offered items not already in the list. Compares both names on both
 * sides, like duplicateIndexOf: a row saves under wikiTitle when it has one.
 */
function missingItems(offered: EditableItem[], existing: EditableItem[]): EditableItem[] {
   const names = (item: EditableItem) =>
      [item.name, item.wikiTitle].filter((name): name is string => !!name).map(normalizeItemName);
   const existingNames = new Set(existing.flatMap(names));
   return offered.filter(item => !names(item).some(name => existingNames.has(name)));
}

/**
 * The shared documents editor over the guide's `{documentid, text,
 * documentType}` rows. Renaming is off: the guide PATCH takes a bare
 * documentid array (GuidesApi::updateGuideDocuments), so a title has nowhere
 * to persist. The type is display-only state seeded from the GET's
 * document_type, or from the picker for a document attached this session.
 */
function DocumentsEditor({
   guide,
   fields,
   onChange,
}: {
   guide: ApiGuide;
   fields: GuideEditFields;
   onChange: Dispatch<SetStateAction<GuideEditFields>>;
}) {
   // Only saved documents are on the guide GET, and only they carry a viewer
   // URL; a document attached this session falls back to the shared row's
   // /Document/{docid} href.
   const rows: DocumentRow[] = fields.documents.map(doc => {
      const saved = guide.documents.find(existing => existing.documentid === doc.documentid);
      return {
         documentid: doc.documentid,
         title: doc.text,
         documentType: doc.documentType,
         url: saved?.url,
      };
   });

   return (
      <DocumentsField
         heading={_js('Documents')}
         documents={rows}
         // Updater form, not a spread of the captured `fields`: the picker
         // roundtrip spans renders, so a reseed landing while it is open
         // would otherwise revert every field this closure didn't touch.
         // The map-back writes keys in initialFields' order, since the dirty
         // compare is JSON.stringify.
         onChange={documents =>
            onChange(current => ({
               ...current,
               documents: documents.map(doc => ({
                  documentid: doc.documentid,
                  text: doc.title,
                  documentType: doc.documentType,
               })),
            }))
         }
         createRow={picked => ({
            documentid: picked.documentid,
            title: picked.title || String(picked.documentid),
            documentType: picked.documentType || undefined,
         })}
      />
   );
}
